// src/pages/Contact.js
import React, { useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import emailjs from "@emailjs/browser";

export default function Contact() {
  const [formData, setFormData] = useState({
    business_category: "",
    full_name: "",
    company_name: "",
    designation: "",
    phone: "",
    email: "",
    message: "",
    terms: false,
  });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus("");
    setLoading(true);

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formData,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setStatus("✅ Message sent successfully!");
        setFormData({
          business_category: "",
          full_name: "",
          company_name: "",
          designation: "",
          phone: "",
          email: "",
          message: "",
          terms: false,
        });
      })
      .catch((error) => {
        console.error("EmailJS Error:", error);
        setStatus("❌ Failed to send message. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      {/* ✅ Hero Banner */}
      <div className="relative h-[300px] md:h-[400px] w-full flex items-center justify-center overflow-hidden">
  {/* Background Image */}
  <img
    src="/images/contact.png"
    alt="Contact Banner"
    className="absolute inset-0 w-full h-full object-cover"
    loading="eager" // forces browser to load immediately
    decoding="async" // improves rendering performance
  />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <h1 className="relative text-4xl md:text-5xl font-bold text-white text-center">
          Contact Us
        </h1>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          {/* Contact Form */}
          <div className="max-w-lg">
            <h3 className="text-2xl font-bold mb-6 text-blue-800">Write to Us</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Business Category */}
              <div>
                <select
                  name="business_category"
                  value={formData.business_category}
                  onChange={handleChange}
                  required
                  className="w-full border-b-2 border-gray-300 focus:border-blue-500 text-gray-900 py-3 px-2 focus:outline-none"
                >
                  <option value="" disabled>
                    Select Business Category
                  </option>
                  <option value="Pharma">Pharma</option>
                  <option value="Chemicals">Chemicals</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              {/* 2 Column Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="Full Name"
                  className="w-full border-b-2 border-gray-300 focus:border-blue-500 py-3 px-2 focus:outline-none"
                />

                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  placeholder="Company Name"
                  className="w-full border-b-2 border-gray-300 focus:border-blue-500 py-3 px-2 focus:outline-none"
                />

                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  required
                  placeholder="Designation"
                  className="w-full border-b-2 border-gray-300 focus:border-blue-500 py-3 px-2 focus:outline-none"
                />

                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Phone Number"
                  className="w-full border-b-2 border-gray-300 focus:border-blue-500 py-3 px-2 focus:outline-none"
                />

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Email"
                  className="w-full border-b-2 border-gray-300 focus:border-blue-500 py-3 px-2 focus:outline-none md:col-span-2"
                />
              </div>

              {/* Message */}
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
                placeholder="Comment or Message"
                className="w-full border-b-2 border-gray-300 focus:border-blue-500 py-3 px-2 focus:outline-none"
              ></textarea>

              {/* Terms */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="terms"
                  checked={formData.terms}
                  onChange={handleChange}
                  required
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="text-gray-700 text-sm">
                  I accept the terms and conditions of this website
                </label>
              </div>

              {/* Submit with Spinner */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center bg-blue-600 text-white py-3 rounded-lg font-semibold 
                  ${
                    loading
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:bg-blue-700 hover:scale-105 hover:shadow-lg"
                  } active:scale-95 transition-all duration-200`}
              >
                {loading ? (
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                ) : null}
                {loading ? "Sending..." : "Send Message"}
              </button>

              {status && (
                <p
                  className={`mt-4 text-center font-medium ${
                    status.includes("✅") ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {status}
                </p>
              )}
            </form>
          </div>

          {/* 🔹 Registered Office & Units */}
          <div className="space-y-6">
            {/* Registered Office */}
            <div className="p-6 border rounded-lg shadow-sm">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Registered Office</h3>
              <p className="flex items-start text-gray-700 mb-2">
                <MapPin className="h-5 w-5 mr-2 text-blue-600 mt-1" />
                1-46/C1/9, Road No.7, Shankar Nagar, Hyderabad, Telangana – 500050, India.
              </p>
              <a
                href="mailto:info@maclabsltd.com"
                className="flex items-center text-gray-700 hover:text-blue-600"
              >
                <Mail className="h-5 w-5 mr-2 text-blue-600" /> info@maclabsltd.com
              </a>
              <a
                href="tel:+919052921590"
                className="flex items-center text-gray-700 hover:text-blue-600"
              >
                <Phone className="h-5 w-5 mr-2 text-blue-600" /> +91 90529 21590
              </a>
            </div>

            {/* Unit I */}
            <div className="p-6 border rounded-lg shadow-sm">
              <h3 className="text-xl font-bold mb-4 text-gray-800">MAC LABS LIMITED: UNIT-I</h3>
              <p className="flex items-start text-gray-700 mb-2">
                <MapPin className="h-5 w-5 mr-2 text-blue-600 mt-1" />
                Plot No. 282, 283 & 284, KIADB, Kolhar Industrial Area, Bidar, Karnataka – India
              </p>
              <a
                href="mailto:sales@maclabsltd.com"
                className="flex items-center text-gray-700 hover:text-blue-600"
              >
                <Mail className="h-5 w-5 mr-2 text-blue-600" /> sales@maclabsltd.com
              </a>
              <a
                href="tel:+917993770901"
                className="flex items-center text-gray-700 hover:text-blue-600"
              >
                <Phone className="h-5 w-5 mr-2 text-blue-600" /> +91 79937 70901
              </a>
            </div>

            {/* Unit II */}
            <div className="p-6 border rounded-lg shadow-sm">
              <h3 className="text-xl font-bold mb-4 text-gray-800">MAC LABS LIMITED: UNIT-II</h3>
              <p className="flex items-start text-gray-700 mb-2">
                <MapPin className="h-5 w-5 mr-2 text-blue-600 mt-1" />
                Plot No. 103D, Sri Venkateswara Industrial Estate, IDA Bollaram, Telangana – India
              </p>
              <a
                href="mailto:Purchase.maclabs@gmail.com"
                className="flex items-center text-gray-700 hover:text-blue-600"
              >
                <Mail className="h-5 w-5 mr-2 text-blue-600" /> Purchase.maclabs@gmail.com
              </a>
              <a
                href="tel:+919700081333"
                className="flex items-center text-gray-700 hover:text-blue-600"
              >
                <Phone className="h-5 w-5 mr-2 text-blue-600" /> +91 97000 81333
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
